// 锁屏密码工具：密码经 SHA-256 哈希后存 localStorage，按用户id区分
// 说明：这是网页隐私锁，用于离开时快速遮挡界面，非账号级安全边界
// 锁屏设置同步到服务端，供手机端H5读取

import http from './httpRequest.js'

const keyOf = (userId) => `lockPwd_${userId}`;

// 计算 SHA-256 十六进制
const sha256 = async (text) => {
	const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
	return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
};

// 是否已设置锁屏密码
export const hasLockPassword = (userId) => {
	return !!localStorage.getItem(keyOf(userId));
};

// 设置/修改锁屏密码
export const setLockPassword = async (userId, password) => {
	const hash = await sha256(String(password));
	localStorage.setItem(keyOf(userId), hash);
	// 同步到服务端
	http({ url: '/user/lockSettings', method: 'put', params: { lockPassword: hash } }).catch(() => {});
};

// 清除锁屏密码
export const clearLockPassword = (userId) => {
	localStorage.removeItem(keyOf(userId));
	// 同步到服务端（传空字符串清除）
	http({ url: '/user/lockSettings', method: 'put', params: { lockPassword: '' } }).catch(() => {});
};

// 校验锁屏密码
export const verifyLockPassword = async (userId, password) => {
	const saved = localStorage.getItem(keyOf(userId));
	if (!saved) {
		// 无密码哈希(老用户同步异常/数据丢失)：自动放行，防止永久死锁
		return true;
	}
	const hash = await sha256(String(password));
	return hash === saved;
};

// ---- 锁屏闲置时间(分钟) ----
const idleKeyOf = (userId) => `lockIdleMinutes_${userId}`;
const DEFAULT_IDLE_MINUTES = 1;

// 获取锁屏闲置时间(分钟), 未设置返回默认值1分钟
export const getIdleMinutes = (userId) => {
	const val = localStorage.getItem(idleKeyOf(userId));
	const minutes = val ? parseInt(val, 10) : DEFAULT_IDLE_MINUTES;
	return (isNaN(minutes) || minutes < 1) ? DEFAULT_IDLE_MINUTES : minutes;
};

// 设置锁屏闲置时间(分钟)
export const setIdleMinutes = (userId, minutes) => {
	const m = parseInt(minutes, 10);
	if (isNaN(m) || m < 1) return;
	localStorage.setItem(idleKeyOf(userId), String(m));
	// 同步到服务端
	http({ url: '/user/lockSettings', method: 'put', params: { lockIdleMinutes: m } }).catch(() => {});
};

// 用服务端锁屏设置校准本地：登录/加载后调用，保证手机端与Web端设置一致
// 以服务端为权威：服务端有密码则覆盖本地，服务端无密码则清除本地
export const syncLockFromServer = (userInfo) => {
	if (!userInfo || !userInfo.id) return;
	const userId = userInfo.id;
	if (userInfo.lockPassword) {
		// 服务端有密码：以服务端为权威覆盖本地
		localStorage.setItem(keyOf(userId), userInfo.lockPassword);
		const minutes = (userInfo.lockIdleMinutes && userInfo.lockIdleMinutes >= 1) ?
			userInfo.lockIdleMinutes : DEFAULT_IDLE_MINUTES;
		localStorage.setItem(idleKeyOf(userId), String(minutes));
	} else {
		// 服务端无密码：清除本地（可能已在其他设备清除，或管理员统一重置）
		localStorage.removeItem(keyOf(userId));
		localStorage.removeItem(idleKeyOf(userId));
	}
};
